/**
 * Scroll + focus resets for full-page flows (e.g. SMB onboarding on one URL) where
 * the document scroll position and iOS input zoom would otherwise carry across steps.
 */

const DEFAULT_VIEWPORT_CONTENT = "width=device-width, initial-scale=1.0";

let iosViewportRestoreTimer: number | null = null;
/** Content to restore after the nudge; shared across overlapping calls so a second call never treats the temporary `maximum-scale=1` string as the restore target. */
let iosViewportContentBeforeNudge: string | null = null;

/** WebKit on iPhone / iPad / iPod; iPadOS desktop UA can report MacIntel + touch. */
export function isLikelyIosWebKit(): boolean {
  if (typeof navigator === "undefined") return false;
  const { userAgent, platform, maxTouchPoints } = navigator;
  if (/iP(hone|ad|od)/i.test(userAgent)) return true;
  return platform === "MacIntel" && maxTouchPoints > 1;
}

export function blurActiveElementIfSafe(): void {
  if (typeof document === "undefined") return;
  const active = document.activeElement;
  if (active instanceof HTMLElement && active !== document.body) {
    active.blur();
  }
}

export function resetDocumentScrollPosition(): void {
  if (typeof window === "undefined") return;
  const root = document.documentElement;
  window.scrollTo(0, 0);
  root.scrollLeft = 0;
  root.scrollTop = 0;
  document.body.scrollLeft = 0;
  document.body.scrollTop = 0;
}

/**
 * iOS Safari can keep “input focus zoom” on the visual viewport after blur + SPA
 * navigation. Toggling viewport `maximum-scale` forces a recalculation; restore
 * quickly so pinch-zoom remains available between navigations.
 */
export function nudgeIosViewportAfterInputZoom(): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  if (!isLikelyIosWebKit()) return;

  const viewport = document.querySelector('meta[name="viewport"]');
  if (!(viewport instanceof HTMLMetaElement)) return;

  if (iosViewportRestoreTimer !== null) {
    window.clearTimeout(iosViewportRestoreTimer);
    iosViewportRestoreTimer = null;
  }

  if (iosViewportContentBeforeNudge === null) {
    iosViewportContentBeforeNudge = viewport.getAttribute("content") ?? DEFAULT_VIEWPORT_CONTENT;
  }

  viewport.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1");
  iosViewportRestoreTimer = window.setTimeout(() => {
    viewport.setAttribute("content", iosViewportContentBeforeNudge ?? DEFAULT_VIEWPORT_CONTENT);
    iosViewportRestoreTimer = null;
    iosViewportContentBeforeNudge = null;
  }, 150);
}

/** Blur then scroll — order helps some mobile browsers release focus zoom before layout. */
export function resetOnboardingStepView(): void {
  blurActiveElementIfSafe();
  resetDocumentScrollPosition();
}

/** Use when leaving a form-heavy screen for another route (e.g. onboarding → dashboard). */
export function resetDocumentViewAfterSpaFormNavigation(): void {
  resetOnboardingStepView();
  nudgeIosViewportAfterInputZoom();
}
